import { Link } from 'react-router-dom';

const Footer: React.FC = () => (
  <footer className="bg-white border-t-4 border-[#003366] text-gray-700">

    {/* Third-party disclaimer banner */}
    <div className="bg-[#003366] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <p className="text-xs text-white/80 text-center leading-relaxed">
          <strong className="text-[#F5A623]">IMPORTANT NOTICE:</strong> Speedy DMV Services is an{' '}
          <strong className="text-white">independent, licensed third-party DMV service provider</strong> and is{' '}
          <strong className="text-white">NOT affiliated with, endorsed by, or part of</strong> the Nevada Department of Motor Vehicles (DMV). We are a private business that assists clients with DMV transactions for a service fee.
        </p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Brand */}
        <div className="lg:col-span-1">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-11 h-11 rounded bg-[#003366] flex items-center justify-center flex-shrink-0">
              <i className="fas fa-car text-[#F5A623] text-lg"></i>
            </div>
            <div>
              <p className="font-bold text-[#003366] text-base">Speedy DMV Services</p>
              <p className="text-[#F5A623] text-xs font-semibold tracking-wide uppercase">Las Vegas, Nevada</p>
            </div>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed mb-5">
            Licensed 3rd party Nevada DMV service provider. We handle vehicle registration, title transfers, license plates, and notary services — fast, professional, and stress-free.
          </p>
          <div className="space-y-2.5">
            {[
              { icon: 'fas fa-map-marker-alt', text: '4525 S Sandhill Rd, Las Vegas, NV 89121' },
              { icon: 'fas fa-phone', text: '702.979.7202', href: 'tel:7029797202' },
              { icon: 'fas fa-clock', text: 'Mon–Fri 9AM–5PM · Sat 10AM–3PM' },
            ].map((item, i) => (
              <div key={i} className="flex items-start space-x-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-[#003366] text-xs`}></i>
                </div>
                {item.href ? (
                  <a href={item.href} className="text-[#003366] hover:text-[#F5A623] text-sm font-semibold transition-colors">{item.text}</a>
                ) : (
                  <span className="text-gray-500 text-sm">{item.text}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Services */}
        <div>
          <h4 className="text-sm font-bold text-[#003366] mb-4 pb-2 border-b-2 border-[#F5A623] uppercase tracking-widest">
            DMV Services
          </h4>
          <ul className="space-y-2">
            {[
              { to: '/vehicle-registration', label: 'Vehicle Registration' },
              { to: '/title-transfer', label: 'Title Transfer' },
              { to: '/license-plates', label: 'License Plates' },
              { to: '/duplicate-title', label: 'Duplicate Title' },
              { to: '/temporary-permits', label: 'Temporary Permits' },
              { to: '/notary-services', label: 'Notary Services' },
              { to: '/services', label: 'All Services' },
            ].map(item => (
              <li key={item.to}>
                <Link to={item.to} className="text-gray-500 hover:text-[#003366] text-sm transition-colors flex items-center space-x-2">
                  <i className="ri-arrow-right-s-line text-[#F5A623] text-xs"></i>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Resources */}
        <div>
          <h4 className="text-sm font-bold text-[#003366] mb-4 pb-2 border-b-2 border-[#F5A623] uppercase tracking-widest">
            DMV Resources
          </h4>
          <ul className="space-y-2">
            {[
              { to: '/nevada-dmv-info', label: 'DMV Info Hub' },
              { to: '/nevada-dmv-forms', label: 'Nevada DMV Forms' },
              { to: '/nevada-vehicle-registration-guide', label: 'Registration Guide' },
              { to: '/nevada-title-transfer-guide', label: 'Title Transfer Guide' },
              { to: '/nevada-smog-emissions-guide', label: 'Emissions Guide' },
              { to: '/nevada-registration-fees', label: 'Fee Guide' },
              { to: '/new-resident', label: 'New to Nevada' },
              { to: '/required-documents', label: 'Required Documents' },
            ].map(item => (
              <li key={item.to}>
                <Link to={item.to} className="text-gray-500 hover:text-[#003366] text-sm transition-colors flex items-center space-x-2">
                  <i className="ri-arrow-right-s-line text-[#F5A623] text-xs"></i>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Links + Hours */}
        <div>
          <h4 className="text-sm font-bold text-[#003366] mb-4 pb-2 border-b-2 border-[#F5A623] uppercase tracking-widest">
            Quick Links
          </h4>
          <ul className="space-y-2 mb-6">
            {[
              { to: '/about', label: 'About Us' },
              { to: '/appointment', label: 'Book Appointment' },
              { to: '/fees', label: 'Service Fees' },
              { to: '/faq', label: 'FAQ' },
              { to: '/location', label: 'Our Location' },
              { to: '/contact', label: 'Contact Us' },
            ].map(item => (
              <li key={item.to}>
                <Link to={item.to} className="text-gray-500 hover:text-[#003366] text-sm transition-colors flex items-center space-x-2">
                  <i className="ri-arrow-right-s-line text-[#F5A623] text-xs"></i>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="bg-gray-50 border border-gray-200 rounded p-4">
            <p className="text-[#003366] text-xs font-bold uppercase tracking-widest mb-2 flex items-center space-x-1.5">
              <i className="fas fa-clock text-[#F5A623]"></i>
              <span>Office Hours</span>
            </p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-gray-400">Mon – Fri</span><span className="text-gray-700 font-medium">9:00 AM – 5:00 PM</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Saturday</span><span className="text-gray-700 font-medium">10:00 AM – 3:00 PM</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Sunday</span><span className="text-gray-400">Closed</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Legal notices */}
    <div className="border-t border-gray-200 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="bg-white border border-gray-200 rounded p-4 mb-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            <strong className="text-[#003366]">Legal Notice:</strong> THE REGISTRANT IS NOT AN ATTORNEY IN THE STATE OF NEVADA AND IS NOT AUTHORIZED TO GIVE LEGAL ADVICE OR LEGAL REPRESENTATION. Speedy DMV Services is a licensed 3rd party service provider not affiliated with the Nevada DMV and cannot change DMV decisions or policies. All information on this website is for general informational purposes only and is not intended as legal advice. DMV requirements, form revisions, and policies change frequently — always verify current requirements at{' '}
            <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="text-[#003366] hover:text-[#F5A623] underline">dmv.nv.gov</a>.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-gray-400">© 2025 Speedy DMV Services — Las Vegas, Nevada. All rights reserved.</p>
          <div className="flex items-center space-x-5">
            <Link to="/contact" className="text-xs text-gray-400 hover:text-[#003366] transition-colors">Privacy Policy</Link>
            <Link to="/contact" className="text-xs text-gray-400 hover:text-[#003366] transition-colors">Terms of Service</Link>
            <a href="https://readdy.ai/?origin=logo" className="text-xs text-gray-400 hover:text-[#003366] transition-colors" target="_blank" rel="noopener noreferrer nofollow">Website Builder</a>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
